import {getActiveSpan, getGlobalScope, getRootSpan, setMeasurement, startInactiveSpan} from '@sentry/react';

import {DBXAbstractPerformanceLogger} from '#core/services/performance-reporting-service/abstract-performance-logger.js';
import {METRIC} from '#core/services/performance-reporting-service/index.js';

// Sentry has limitation of 10 measurements per transaction
// We need to filter out some of them to not exceed this limit
// TODO: Find better solution
const allowedMeasurements = [
    METRIC.JS_LOAD_TIME,
    METRIC.INIT_START,
    METRIC.INIT_DURATION,
    METRIC.RENDER_START,
    METRIC.MOJ_TIMINGS_GET_CONFIG_DURATION,
    METRIC.INITIAL_PAGE_WS_DURATION,
    METRIC.INITIAL_PAGE_WS_TTFB,
    METRIC.INITIAL_PAGE_WS_TTFS,
    METRIC.INITIAL_PAGE_FULL_LOAD_TIME,

    METRIC.INITIAL_PAGE_READY,
    METRIC.INITIAL_PAGE_INTERACTED_BEFORE_READY,

    METRIC.PAGE_WS_DURATION,
];
class PerfLoggerImpl extends DBXAbstractPerformanceLogger {
    constructor() {
        super();
        this.span = null;
        this.initializedOnSportsbookUrl = false;
        this.reportedMetrics = new Set();
    }

    setTags(tags) {
        super.setTags(tags);
        this.initializedOnSportsbookUrl = this.tags.initializedOnSportsbookUrl;
        delete this.tags.initializedOnSportsbookUrl;

        getGlobalScope().setTags(this.tags);
    }

    reportTiming(name, timestamp) {
        if (!this.initializedOnSportsbookUrl) {
            // Sportsbook is initialized on some 'casino' url
            // Ignore this
            return;
        }
        this.reportMetric(name, timestamp, 'millisecond');
    }

    reportDuration(name, duration) {
        if (!this.initializedOnSportsbookUrl) {
            // Sportsbook is initialized on some 'casino' url
            // Ignore this
            return;
        }
        this.reportMetric(name, duration, 'millisecond');
    }

    reportMetric(name, value, unit) {
        if (!this.initializedOnSportsbookUrl) {
            // Sportsbook is initialized on some 'casino' url
            // Ignore this
            return;
        }
        // Treat we interested in only initial page load metrics
        // In other cases this logic will not work correctly
        if (this.reportedMetrics.has(name)) return; // Do not report same metric twice
        this.reportedMetrics.add(name);

        const transformedName = name.replaceAll('-', '_');

        const activeSpan = getActiveSpan();
        const rootSpan = activeSpan ? getRootSpan(activeSpan) : undefined;
        rootSpan?.setAttribute(transformedName, value);

        if (!allowedMeasurements.includes(name)) return; // push only allowed measurements
        setMeasurement(transformedName, value, unit);
    }

    /**
     * Called when first WS subscription created
     * @param path {string}
     */
    wsActivityStarted(path) {
        if (this.span) {
            // Should not fall here, but just to be reinsured
            this.span.end();
            this.span = null;
        }
        if (!this.initializedOnSportsbookUrl) return;

        this.span = startInactiveSpan({name: `WS fetching data for ${path}`, onlyIfParent: true});
    }

    /**
     * Called to log collected performance statistics
     * @param data {object}
     */
    wsActivityData(data) {
        if (!this.span || this.span.endTimestamp) return; // Span is ended. Do not report anything

        // Previously mojito had limited reporting capabilities, and reporting was slightly incorrect (it covers also WS processing time)
        // So we do the trick to report same numbers to Sentry
        const wsDuration = data.timeToLastWSSubscriptionProcessed - data.timeToFirstWSByte;

        if (
            wsDuration > 30000 ||
            (data.initialLoading && data.timeToFirstWSByte > 30000) ||
            (data.initialLoading && !this.initializedOnSportsbookUrl)
        ) {
            // User probably lock phone during WS fetching
            // Or sportsbook is initialized on some 'casino' url
            // Ignore these stats, as it is not representative
            return;
        }

        if (data.initialLoading) {
            this.reportMetric(
                METRIC.INITIAL_PAGE_WS_CONNECTING_DURATION,
                data.timeToWSConnectionReady - data.timeToWSConnectionStarted,
                'millisecond'
            );
            this.reportMetric(METRIC.INITIAL_PAGE_WS_DURATION, wsDuration, 'millisecond');
            this.reportMetric(METRIC.INITIAL_PAGE_WS_TTFB, data.timeToFirstWSByte, 'millisecond'); // From page navigation
            this.reportMetric(METRIC.INITIAL_PAGE_WS_TTFS, data.timeToFirstWSSubscription, 'millisecond');
            this.reportMetric(METRIC.INITIAL_PAGE_WS_SUBSCRIPTIONS_COUNT, data.totalWSSubscriptions);
            this.reportMetric(METRIC.INITIAL_PAGE_WS_STALE_SUBSCRIPTIONS_COUNT, data.staleWSSubscriptions);
            this.reportMetric(METRIC.INITIAL_PAGE_FULL_LOAD_TIME, data.timeToFirstWSByte + wsDuration, 'millisecond');

            if (this.span) {
                this.span.setAttributes({
                    ws_duration: wsDuration,
                    ws_ttfb: data.timeToFirstWSByte,
                    ws_ttfs: data.timeToFirstWSSubscription,
                    ws_ttlb: data.timeToLastWSByte,
                    ws_ttls: data.timeToLastWSSubscriptionProcessed,
                    ws_subscriptions_count: data.totalWSSubscriptions,
                    ws_stale_subscriptions_count: data.staleWSSubscriptions,
                    ws_connecting_duration: data.timeToWSConnectionReady - data.timeToWSConnectionStarted,
                });
            }
        } else {
            this.reportMetric(METRIC.PAGE_WS_DURATION, data.totalWSDuration, 'millisecond');
            this.reportMetric(METRIC.PAGE_WS_SUBSCRIPTIONS_COUNT, data.totalWSSubscriptions);
            this.reportMetric(METRIC.PAGE_WS_STALE_SUBSCRIPTIONS_COUNT, data.staleWSSubscriptions);

            if (this.span) {
                this.span.setAttributes({
                    ws_duration: data.totalWSDuration,
                    ws_subscriptions_count: data.totalWSSubscriptions,
                    ws_stale_subscriptions_count: data.staleWSSubscriptions,
                });
            }
        }
    }

    /**
     * Called when all subscriptions received at least one response.
     * It also may be called to close corresponding wsActivityStarted, even if no websocket activity
     */
    wsActivityMostlyFinished() {
        this.span && this.span.end();
        this.span = null;
    }
}

export const DBXSentryPerformanceLogger = new PerfLoggerImpl();
